import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ServiceTypes } from '@guryou/core/build/entity';
import { DefaultText } from '@guryou/html-components';
import { stateIsLoading } from '@guryou/redux';
import { WidgetContext } from './WidgetContext';
import WidgetMemberships from './WidgetMemberships';
import WidgetNotPaidFor from './WidgetNotPaidFor';
import WidgetServices from './WidgetServices';
import WidgetVideos from './WidgetVideos';
import { Icon } from '../../components/shared';

const Widget = ({ fullWidth = false }) => {
  const { t } = useTranslation();
  const {
    activeTab,
    setActiveTab,
    showEvents,
    showVideos,
    showCourses,
    showServices,
    showMemberships,
    plan,
    services,
    courses,
    memberships,
    primaryColor,
    fontF,
    fontS,
  } = useContext(WidgetContext);

  // Use custom primary color if available, otherwise fallback to default mandy color
  const primary = primaryColor || 'var(--feel-primary)';

  // Apply custom fonts
  const fontFamilyStyle = fontF && fontF !== 'Metropolis' ? { fontFamily: fontF } : {};

  // Apply font style (italic, bold, etc.)
  let fontStyleCSS = {};
  if (fontS && fontS !== 'sans-serif') {
    if (fontS === 'italic') {
      fontStyleCSS = { fontStyle: 'italic' };
    } else if (fontS === 'bold') {
      fontStyleCSS = { fontWeight: 'bold' };
    } else if (fontS === 'Double-struck') {
      fontStyleCSS = { fontWeight: 'bold' };
    }
  }

  const videos = useSelector(state => state.widget.videos);
  const tabsLength = [showEvents, showVideos, showCourses, showServices, showMemberships].filter(x => x).length;
  if (!tabsLength) {
    return <DefaultText>{t('BOOK_WIDGET.configurationWidg')}</DefaultText>;
  }

  if (plan.id === 0) {
    return <WidgetNotPaidFor />;
  }

  const containerClass = fullWidth ? 'w-full px-4 sm:px-6 lg:px-8' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8';

  if (tabsLength === 1) {
    if (showServices && !showCourses && !showEvents) {
      return <WidgetServices />;
    }
    if (showCourses && !showServices && !showEvents) {
      return <WidgetServices courseView={true} />;
    }
    if (showEvents && !showServices && !showCourses) {
      return <WidgetServices eventView={true} />;
    }
    if (showMemberships) {
      return <WidgetMemberships />;
    }
    return <WidgetVideos />;
  }

  if (!activeTab && (stateIsLoading([services]) || stateIsLoading([courses]) || stateIsLoading([videos]))) {
    return (
      <div className="min-h-screen">
        {/* Header Skeleton */}
        <div className="bg-white">
          <div className={containerClass}>
            <div className="flex justify-center py-3">
              <div className="flex space-x-1 bg-gray-100 rounded-lg p-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="w-12 h-12 bg-gray-200 rounded-md animate-pulse"></div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Content Skeleton - Widget Layout */}
        <div className={`${containerClass} py-8`}>
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Skeleton */}
            <div className="lg:w-80 flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <div className="flex items-center mb-4">
                  <div className="w-6 h-6 bg-gray-200 rounded animate-pulse mr-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
                </div>
                <div className="space-y-2">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="flex items-center justify-between p-4 bg-gray-100 rounded-lg animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                      <div className="w-8 h-6 bg-gray-200 rounded-full"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content Skeleton */}
            <div className="flex-1">
              {/* Header Section Skeleton */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse mr-4"></div>
                  <div>
                    <div className="h-8 bg-gray-200 rounded w-48 mb-2 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-64 animate-pulse"></div>
                  </div>
                </div>
              </div>

              {/* Services Grid Skeleton */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                    <div className="flex items-center mb-4">
                      <div className="flex space-x-1 mr-3">
                        {[1, 2, 3, 4, 5].map(star => (
                          <div key={star} className="w-4 h-4 bg-gray-200 rounded"></div>
                        ))}
                      </div>
                      <div className="w-12 h-6 bg-gray-200 rounded"></div>
                    </div>
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-gray-200 rounded mr-3"></div>
                        <div className="h-4 bg-gray-200 rounded w-20"></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="h-8 bg-gray-200 rounded w-16"></div>
                      <div className="h-10 bg-gray-200 rounded w-24"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ ...fontFamilyStyle, ...fontStyleCSS }}>
      {/* Header with Navigation Tabs */}
      <div className="bg-white">
        <div className={containerClass}>
          {/* Mobile: Compact tabs with icons and labels */}
          <div className="block lg:hidden">
            <nav className="flex justify-center py-3" aria-label="Tabs">
              <div className="flex justify-between space-x-1 w-full bg-gray-100 rounded-lg p-2">
                {showServices && services.data?.length ? (
                  <button
                    onClick={() => setActiveTab('service')}
                    className={`flex flex-col items-center justify-center px-2 py-2 rounded-md transition-all duration-200 min-w-0 flex-1 ${
                      activeTab === 'service' ? `text-white shadow-md` : `text-gray-600 hover:text-white hover:shadow-md`
                    }`}
                    style={{
                      backgroundColor: activeTab === 'service' ? primary : 'transparent',
                    }}
                    onMouseEnter={e => {
                      if (activeTab !== 'service') {
                        e.currentTarget.style.backgroundColor = primary;
                      }
                    }}
                    onMouseLeave={e => {
                      if (activeTab !== 'service') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Icon name="book" size="sm" className="mb-1" />
                    <span className="text-2xs font-medium truncate w-full text-center" style={{ fontSize: '6px' }}>
                      {t('SERVICES.titlePlural').toLowerCase()}
                    </span>
                  </button>
                ) : null}

                {showCourses && courses.data?.filter(c => c.serviceType === ServiceTypes.course).length ? (
                  <button
                    onClick={() => setActiveTab('course')}
                    className={`flex flex-col items-center justify-center px-2 py-2 rounded-md transition-all duration-200 min-w-0 flex-1 ${
                      activeTab === 'course' ? `text-white shadow-md` : `text-gray-600 hover:text-white hover:shadow-md`
                    }`}
                    style={{
                      backgroundColor: activeTab === 'course' ? primary : 'transparent',
                    }}
                    onMouseEnter={e => {
                      if (activeTab !== 'course') {
                        e.currentTarget.style.backgroundColor = primary;
                      }
                    }}
                    onMouseLeave={e => {
                      if (activeTab !== 'course') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Icon name="read" size="sm" className="mb-1" />
                    <span className="text-2xs font-medium truncate w-full text-center" style={{ fontSize: '6px' }}>
                      {t('COURSES.titlePlural').toLowerCase()}
                    </span>
                  </button>
                ) : null}

                {showVideos && videos.data?.length ? (
                  <button
                    onClick={() => setActiveTab('video')}
                    className={`flex flex-col items-center justify-center px-2 py-2 rounded-md transition-all duration-200 min-w-0 flex-1 ${
                      activeTab === 'video' ? `text-white shadow-md` : `text-gray-600 hover:text-white hover:shadow-md`
                    }`}
                    style={{
                      backgroundColor: activeTab === 'video' ? primary : 'transparent',
                    }}
                    onMouseEnter={e => {
                      if (activeTab !== 'video') {
                        e.currentTarget.style.backgroundColor = primary;
                      }
                    }}
                    onMouseLeave={e => {
                      if (activeTab !== 'video') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Icon name="video" size="sm" className="mb-1" />
                    <span className="text-2xs font-medium truncate w-full text-center" style={{ fontSize: '6px' }}>
                      {t('PRODUCT_VIDEO.titlePlural').toLowerCase()}
                    </span>
                  </button>
                ) : null}

                {showMemberships && memberships?.length ? (
                  <button
                    onClick={() => setActiveTab('membership')}
                    className={`flex flex-col items-center justify-center px-2 py-2 rounded-md transition-all duration-200 min-w-0 flex-1 ${
                      activeTab === 'membership' ? `text-white shadow-md` : `text-gray-600 hover:text-white hover:shadow-md`
                    }`}
                    style={{
                      backgroundColor: activeTab === 'membership' ? primary : 'transparent',
                    }}
                    onMouseEnter={e => {
                      if (activeTab !== 'membership') {
                        e.currentTarget.style.backgroundColor = primary;
                      }
                    }}
                    onMouseLeave={e => {
                      if (activeTab !== 'membership') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Icon name="business" size="sm" className="mb-1" />
                    <span className="text-2xs font-medium truncate w-full text-center" style={{ fontSize: '6px' }}>
                      {t('MEMBERSHIPS.titlePlural').toLowerCase()}
                    </span>
                  </button>
                ) : null}

                {showEvents && courses.data?.filter(c => c.serviceType === ServiceTypes.event).length ? (
                  <button
                    onClick={() => setActiveTab('event')}
                    className={`flex flex-col items-center justify-center px-2 py-2 rounded-md transition-all duration-200 min-w-0 flex-1 ${
                      activeTab === 'event' ? `text-white shadow-md` : `text-gray-600 hover:text-white hover:shadow-md`
                    }`}
                    style={{
                      backgroundColor: activeTab === 'event' ? primary : 'transparent',
                    }}
                    onMouseEnter={e => {
                      if (activeTab !== 'event') {
                        e.currentTarget.style.backgroundColor = primary;
                      }
                    }}
                    onMouseLeave={e => {
                      if (activeTab !== 'event') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Icon name="calendar" size="sm" className="mb-1" />
                    <span className="text-2xs font-medium truncate w-full text-center" style={{ fontSize: '6px' }}>
                      {t('EVENTS.titlePlural').toLowerCase()}
                    </span>
                  </button>
                ) : null}
              </div>
            </nav>
          </div>

          {/* Desktop: Centered tabs */}
          <div className="hidden lg:block">
            <div className="flex justify-center">
              <nav className="flex space-x-8 py-4" aria-label="Tabs">
                {showServices && services.data?.length ? (
                  <button
                    onClick={() => setActiveTab('service')}
                    className={`group inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      activeTab === 'service' ? `text-white shadow-lg` : `text-gray-600 hover:text-white hover:shadow-lg`
                    }`}
                    style={{
                      backgroundColor: activeTab === 'service' ? primary : 'transparent',
                    }}
                    onMouseEnter={e => {
                      if (activeTab !== 'service') {
                        e.currentTarget.style.backgroundColor = primary;
                      }
                    }}
                    onMouseLeave={e => {
                      if (activeTab !== 'service') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Icon name="book" size="sm" className="mr-2" />
                    <span className="capitalize">{t('SERVICES.titlePlural').toLowerCase()}</span>
                  </button>
                ) : null}

                {showCourses && courses.data?.filter(c => c.serviceType === ServiceTypes.course).length ? (
                  <button
                    onClick={() => setActiveTab('course')}
                    className={`group inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      activeTab === 'course' ? `text-white shadow-lg` : `text-gray-600 hover:text-white hover:shadow-lg`
                    }`}
                    style={{
                      backgroundColor: activeTab === 'course' ? primary : 'transparent',
                    }}
                    onMouseEnter={e => {
                      if (activeTab !== 'course') {
                        e.currentTarget.style.backgroundColor = primary;
                      }
                    }}
                    onMouseLeave={e => {
                      if (activeTab !== 'course') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Icon name="read" size="sm" className="mr-2" />
                    <span className="capitalize">{t('COURSES.titlePlural').toLowerCase()}</span>
                  </button>
                ) : null}

                {showVideos && videos.data?.length ? (
                  <button
                    onClick={() => setActiveTab('video')}
                    className={`group inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      activeTab === 'video' ? `text-white shadow-lg` : `text-gray-600 hover:text-white hover:shadow-lg`
                    }`}
                    style={{
                      backgroundColor: activeTab === 'video' ? primary : 'transparent',
                    }}
                    onMouseEnter={e => {
                      if (activeTab !== 'video') {
                        e.currentTarget.style.backgroundColor = primary;
                      }
                    }}
                    onMouseLeave={e => {
                      if (activeTab !== 'video') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Icon name="video" size="sm" className="mr-2" />
                    <span className="capitalize">{t('PRODUCT_VIDEO.titlePlural').toLowerCase()}</span>
                  </button>
                ) : null}

                {showMemberships && memberships?.length ? (
                  <button
                    onClick={() => setActiveTab('membership')}
                    className={`group inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      activeTab === 'membership' ? `text-white shadow-lg` : `text-gray-600 hover:text-white hover:shadow-lg`
                    }`}
                    style={{
                      backgroundColor: activeTab === 'membership' ? primary : 'transparent',
                    }}
                    onMouseEnter={e => {
                      if (activeTab !== 'membership') {
                        e.currentTarget.style.backgroundColor = primary;
                      }
                    }}
                    onMouseLeave={e => {
                      if (activeTab !== 'membership') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Icon name="business" size="sm" className="mr-2" />
                    <span className="capitalize">{t('MEMBERSHIPS.titlePlural').toLowerCase()}</span>
                  </button>
                ) : null}

                {showEvents && courses.data?.filter(c => c.serviceType === ServiceTypes.event).length ? (
                  <button
                    onClick={() => setActiveTab('event')}
                    className={`group inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      activeTab === 'event' ? `text-white shadow-lg` : `text-gray-600 hover:text-white hover:shadow-lg`
                    }`}
                    style={{
                      backgroundColor: activeTab === 'event' ? primary : 'transparent',
                    }}
                    onMouseEnter={e => {
                      if (activeTab !== 'event') {
                        e.currentTarget.style.backgroundColor = primary;
                      }
                    }}
                    onMouseLeave={e => {
                      if (activeTab !== 'event') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <Icon name="calendar" size="sm" className="mr-2" />
                    <span className="capitalize">{t('EVENTS.titlePlural').toLowerCase()}</span>
                  </button>
                ) : null}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`${containerClass} py-8`}>
        <div className="rounded-2xl overflow-hidden">
          {activeTab === 'service' && <WidgetServices />}
          {activeTab === 'course' && <WidgetServices courseView={true} />}
          {activeTab === 'video' && <WidgetVideos />}
          {activeTab === 'membership' && <WidgetMemberships />}
          {activeTab === 'event' && <WidgetServices eventView={true} />}
        </div>
      </div>
    </div>
  );
};

export default Widget;
